import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Alert,
  Grid,
  Card,
  CardContent,
  CircularProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Refresh as RefreshIcon,
  Stop as StopIcon,
  PlayArrow as PlayArrowIcon,
  Engineering as EngineeringIcon,
  Speed as SpeedIcon,
  Warning as WarningIcon,
  DeleteSweep as RejectIcon
} from '@mui/icons-material';
import axios from 'axios';
import { io } from 'socket.io-client';

const ProductionMonitoring = () => {
  const [productionOrders, setProductionOrders] = useState([]);
  const [machineStatus, setMachineStatus] = useState({
    connected: false,
    status: 'disconnected',
    current_order: null,
    total_produced: 0,
    production_rate: 0,
    last_update: null
  });
  const [loading, setLoading] = useState(true);
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    loadProductionOrders();
    loadMachineStatus();
    
    // Connessione WebSocket per aggiornamenti real-time
    const newSocket = io();
    setSocket(newSocket);

    newSocket.on('machineStatusUpdate', (status) => {
      setMachineStatus(status);
    });

    newSocket.on('productionUpdate', (data) => {
      loadProductionOrders();
      if (data.machineStatus) {
        setMachineStatus(data.machineStatus);
      }
    });

    return () => {
      newSocket.disconnect();
    };
  }, []);

  const loadProductionOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/orders/production');
      setProductionOrders(response.data);
    } catch (error) {
      console.error('Errore nel caricamento ordini in produzione:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMachineStatus = async () => {
    try {
      const response = await axios.get('/api/machine-status');
      setMachineStatus(response.data);
    } catch (error) {
      console.error('Errore nel caricamento stato macchina:', error);
    }
  };

  const handleStopProduction = async (orderId) => {
    if (window.confirm('Sei sicuro di voler fermare la produzione di questo ordine?')) {
      try {
        await axios.post(`/api/orders/${orderId}/stop-production`);
        loadProductionOrders();
      } catch (error) {
        console.error('Errore nel fermare la produzione:', error);
      }
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      'in_production': { label: 'In Produzione', color: 'primary' },
      'sent_to_machine': { label: 'Inviato a Macchina', color: 'info' },
      'paused': { label: 'In Pausa', color: 'warning' },
      'completed': { label: 'Completato', color: 'success' },
      'error': { label: 'Errore', color: 'error' }
    };

    const config = statusConfig[status] || statusConfig['in_production'];
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  // Bionen-specific status mapping
  const getBionenStatusChip = (status) => {
    const statusConfig = {
      'start': { label: 'START', color: 'success' },
      'stop': { label: 'STOP', color: 'warning' },
      'reset': { label: 'RESET', color: 'info' },
      'error': { label: 'ERRORE', color: 'error' },
      'connected': { label: 'Connessa', color: 'success' },
      'running': { label: 'In Esecuzione', color: 'primary' },
      'idle': { label: 'Inattiva', color: 'default' },
      'disconnected': { label: 'Disconnessa', color: 'error' }
    };

    const config = statusConfig[status] || statusConfig['disconnected'];
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const getMachineStatusChip = (status) => {
    const statusConfig = {
      'connected': { label: 'Connessa', color: 'success' },
      'running': { label: 'In Esecuzione', color: 'primary' },
      'idle': { label: 'Inattiva', color: 'default' },
      'error': { label: 'Errore', color: 'error' },
      'disconnected': { label: 'Disconnessa', color: 'error' }
    };

    const config = statusConfig[status] || statusConfig['disconnected'];
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const calculateProgress = (produced, total) => {
    return total > 0 ? Math.min((produced / total) * 100, 100) : 0;
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 90 },
    { field: 'product_code', headerName: 'Codice Prodotto', width: 150 },
    { 
      field: 'quantity', 
      headerName: 'Quantità Target', 
      width: 120, 
      type: 'number' 
    },
    { 
      field: 'produced_quantity', 
      headerName: 'Prodotte', 
      width: 100, 
      type: 'number',
      valueGetter: (params) => params.row.produced_quantity || 0
    },
    {
      field: 'progress',
      headerName: 'Progresso',
      width: 150,
      renderCell: (params) => {
        const progress = calculateProgress(
          params.row.produced_quantity || 0,
          params.row.quantity
        );
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ width: '100%', mr: 1 }}>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
              <Typography variant="body2" color="text.secondary">
                {Math.round(progress)}%
              </Typography>
            </Box>
          </Box>
        );
      }
    },
    {
      field: 'status',
      headerName: 'Stato',
      width: 130,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'sent_to_production_at',
      headerName: 'Inizio Produzione',
      width: 150,
      valueFormatter: (params) => {
        return params.value ? new Date(params.value).toLocaleString('it-IT') : '-';
      }
    },
    {
      field: 'estimated_completion',
      headerName: 'Completamento Stimato',
      width: 180,
      valueGetter: (params) => {
        if (!params.row.production_rate || !params.row.sent_to_production_at) {
          return 'N/A';
        }
        
        const remaining = params.row.quantity - (params.row.produced_quantity || 0);
        const hoursRemaining = remaining / params.row.production_rate;
        const estimatedTime = new Date(Date.now() + hoursRemaining * 60 * 60 * 1000);
        
        return estimatedTime.toLocaleString('it-IT');
      }
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 100,
      sortable: false,
      renderCell: (params) => {
        if (params.row.status === 'in_production') {
          return (
            <Tooltip title="Ferma produzione">
              <IconButton
                size="small"
                color="error"
                onClick={() => handleStopProduction(params.row.id)}
              >
                <StopIcon />
              </IconButton>
            </Tooltip>
          );
        }
        return null;
      }
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Stato Macchina */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EngineeringIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2">
                  Connessione OPC UA
                </Typography>
              </Box>
              {getMachineStatusChip(machineStatus.connected ? 'connected' : 'disconnected')}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EngineeringIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2">
                  Stato Macchina
                </Typography>
              </Box>
              {machineStatus.bionenStatus
                ? getBionenStatusChip(machineStatus.bionenStatus.status)
                : getMachineStatusChip(machineStatus.status)}
              {machineStatus.bionenStatus?.description && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {machineStatus.bionenStatus.description}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PlayArrowIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2">
                  Ordine Corrente
                </Typography>
              </Box>
              <Typography variant="h6">
                {machineStatus.currentOrderId || 'Nessuno'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SpeedIcon sx={{ mr: 1 }} />
                <Typography variant="subtitle2">
                  Velocità
                </Typography>
              </Box>
              <Typography variant="h6">
                {machineStatus.speed || 0} pz/min
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Efficienza
              </Typography>
              <Typography variant="h6" color="text.secondary">
                {machineStatus.efficiency ? `${machineStatus.efficiency.toFixed(1)}%` : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bionen: Allarmi Attivi */}
      {machineStatus.bionenAlarms?.hasAlarms && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<WarningIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Allarmi Attivi ({machineStatus.bionenAlarms.activeAlarms.length})
          </Typography>
          {machineStatus.bionenAlarms.activeAlarms.map((alarm, idx) => (
            <Chip
              key={idx}
              label={alarm.description}
              color="error"
              size="small"
              variant="outlined"
              sx={{ mr: 0.5, mb: 0.5 }}
            />
          ))}
        </Alert>
      )}

      {/* Bionen: Scarti per Tipologia */}
      {machineStatus.bionenRejects && Object.keys(machineStatus.bionenRejects).some(k => machineStatus.bionenRejects[k]?.count > 0) && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<RejectIcon />}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Scarti per Tipologia
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {Object.entries(machineStatus.bionenRejects)
              .filter(([, data]) => data?.count > 0)
              .map(([key, data]) => (
                <Chip
                  key={key}
                  label={`${data.description}: ${data.count}`}
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              ))}
          </Box>
        </Alert>
      )}

      {/* Alert di stato */}
      {!machineStatus.connected && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Macchina non connessa. I dati potrebbero non essere aggiornati.
        </Alert>
      )}

      {machineStatus.status === 'error' && machineStatus.latest_data?.machine_data?.errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Errore Macchina:</strong> {machineStatus.latest_data.machine_data.errorMessage}
        </Alert>
      )}

      {/* Tabella Produzione */}
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Monitoraggio Produzione
          </Typography>
          <IconButton onClick={loadProductionOrders}>
            <RefreshIcon />
          </IconButton>
        </Box>

        <Box sx={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={productionOrders}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25]}
            disableSelectionOnClick
            localeText={{
              noRowsLabel: 'Nessun ordine in produzione',
              footerRowSelected: (count) => `${count} riga${count !== 1 ? 'he' : ''} selezionata${count !== 1 ? 'e' : ''}`,
              footerTotalRows: 'Righe totali:',
              columnMenuSortAsc: 'Ordine crescente',
              columnMenuSortDesc: 'Ordine decrescente',
              columnMenuFilter: 'Filtro',
              columnMenuHideColumn: 'Nascondi colonna',
              columnMenuShowColumns: 'Mostra colonne'
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default ProductionMonitoring;